/**
 * The plains map at 256 × 256 m (ลานศิลาเก้ากระบี่): the approved layout as data (lane C0-P1, build-order step 1 of
 * docs/design/C0_TOWN_LAYOUT.md §8). Ported from the plan's sketch `scripts/lanes/c0-town/layout.mjs`, with the
 * places the user added on 2026-10-10: the warehouse (โรงคลัง) on the market street next to the plaza, the training
 * hall (โรงฝึกยุทธ์) in place of the yard, guard posts and two patrol loops, and horse stations (the carriage stop and
 * each road that leaves the map).
 *
 * Map metres: x east, y south, 1 tile = 1 m, north up. Data only: `plainsMap.ts` lays the tiles from it, behind the
 * `PLAINS256` switch (data/zones.ts); safe circles are in data/safeZones.ts (`PLAINS_SAFE_ZONES`, from `SAFE` here).
 */

import { buildOuter } from './plainsOuter';

export const PLAINS_SIZE = 256;

export type XY = readonly [x: number, y: number];
/** A rectangle by its centre and size: [cx, cy, w, h] (metres). */
export type CRect = readonly [cx: number, cy: number, w: number, h: number];
/** A rectangle by its sides: [x0, x1, y0, y1) (metres). */
export type Box = readonly [x0: number, x1: number, y0: number, y1: number];

export const STELE: XY = [128, 128];
/** The wake spot of the opening (inside the south paifang, out of its occlusion band). */
export const WAKE: XY = [128, 145];
export const ELDER: XY = [131, 125];

/** Ground kinds the map generator knows how to lay. */
export type AreaKind =
  | 'cliff'
  | 'bamboo'
  | 'pine'
  | 'field'
  | 'ring'
  | 'ruin'
  | 'marsh'
  | 'hill'
  | 'farm'
  | 'grave'
  | 'ledge'
  | 'garden'
  | 'clearing'
  | 'pond'
  | 'paved'
  | 'street'
  | 'homesGround'
  | 'yardDirt'
  | 'terrace';

export interface AreaDef {
  /** Row of the places table in C0_OPENING_PLAINS §4.3 (0: none). */
  readonly n: number;
  readonly id: string;
  /** Thai name (map labels). */
  readonly name: string;
  readonly kind: AreaKind;
  /** Rectangle [cx, cy, w, h], or a circle `c` + `r`. */
  readonly rect?: CRect;
  readonly c?: XY;
  readonly r?: number;
  /** A circle kept clear of trees and rocks round it (metres). */
  readonly clear?: number;
  /** Fights happen here (sparring, dummies). */
  readonly fight?: boolean;
}

export const AREAS: readonly AreaDef[] = [
  { n: 23, id: 'cleft', name: 'ผาร่องกระบี่', kind: 'cliff', rect: [128, 4, 256, 8] },
  { n: 0, id: 'eastcliff', name: 'ผาตะวันออก', kind: 'cliff', rect: [252, 128, 8, 240] },
  { n: 20, id: 'bamboo', name: 'ป่าไผ่เหนือ', kind: 'bamboo', rect: [50, 36, 60, 44] },
  { n: 14, id: 'wood', name: 'ป่าต้นน้ำ', kind: 'pine', rect: [204, 40, 64, 52] },
  { n: 21, id: 'apefield', name: 'ทุ่งลมหวน', kind: 'field', c: [128, 62], r: 20, clear: 26 },
  { n: 22, id: 'demonring', name: 'ลานจอมมาร', kind: 'ring', c: [128, 22], r: 16 },
  { n: 16, id: 'temple', name: 'วัดร้างระฆังร้าว', kind: 'ruin', rect: [30, 116, 40, 40] },
  { n: 24, id: 'marsh', name: 'บึงหมอก', kind: 'marsh', rect: [30, 164, 30, 24] },
  { n: 15, id: 'hill', name: 'เนินโจร', kind: 'hill', rect: [52, 204, 64, 56] },
  { n: 12, id: 'farm', name: 'นาขั้นบันได', kind: 'farm', rect: [128, 202, 60, 30] },
  { n: 18, id: 'graves', name: 'สุสานเนินหมอก', kind: 'grave', rect: [190, 214, 44, 36] },
  { n: 19, id: 'redcliff', name: 'ผาแดง · ลานเลือด', kind: 'ledge', c: [230, 232], r: 10 },
  { n: 17, id: 'ridge', name: 'ป่าสนผาหมี', kind: 'pine', rect: [230, 118, 36, 64] },
  { n: 25, id: 'garden', name: 'สวนเจดีย์', kind: 'garden', rect: [180, 82, 24, 24] },
  { n: 13, id: 'shrine', name: 'ศาลร้างกลางทุ่ง', kind: 'clearing', rect: [204, 152, 12, 12] },
  { n: 9, id: 'pond', name: 'บ่อบัวใต้สะพาน', kind: 'pond', rect: [164, 148, 20, 14] },
  // The yard of the plan, now the training hall's court (โรงฝึกยุทธ์, user 2026-10-10).
  { n: 5, id: 'trainhall', name: 'โรงฝึกยุทธ์', kind: 'paved', rect: [86, 116, 26, 18], fight: true },
  { n: 6, id: 'market', name: 'ถนนตลาด', kind: 'street', rect: [182, 124, 48, 8] },
  { n: 10, id: 'homes', name: 'ย่านบ้านเรือน', kind: 'homesGround', rect: [98, 156, 40, 26] },
  { n: 11, id: 'carriage', name: 'ท่ารถม้า', kind: 'yardDirt', rect: [110, 178, 20, 14] },
  { n: 4, id: 'terrace', name: 'ลานหน้าหอธงสำนัก', kind: 'terrace', rect: [84, 76, 34, 24] },
  { n: 26, id: 'jail', name: 'คุกหลังหอ', kind: 'yardDirt', rect: [54, 76, 12, 12] },
  { n: 0, id: 'opera', name: 'ลานงิ้ว', kind: 'paved', rect: [166, 104, 12, 10] },
  { n: 2, id: 'apron', name: 'ลานหน้าซุ้มประตู', kind: 'paved', rect: [128, 150, 10, 10] },
];

/** The stream (a polyline, 5 m wide) from the north cliff to the east edge. */
export const STREAM: readonly XY[] = [
  [178, 0], [174, 22], [170, 44], [162, 66], [154, 92], [150, 112], [150, 126], [150, 140], [154, 156], [162, 172],
  [186, 184], [222, 194], [256, 198],
];
export const STREAM_WIDTH = 5;
/** The bridge on the east road (planks over the stream) and the ford's stepping stones. */
export const BRIDGE: { readonly at: XY; readonly w: number; readonly h: number } = { at: [150, 126], w: 10, h: 6 };
export const FORD: XY = [162, 180];

export interface GateDef {
  readonly at: XY;
  readonly name: string;
  /** A water gate (where the stream passes the hub wall). */
  readonly water?: boolean;
}
export const GATES: readonly GateDef[] = [
  { at: [128, 92], name: 'ประตูเหนือ' },
  { at: [62, 128], name: 'ประตูตะวันตก' },
  { at: [210, 124], name: 'ประตูตะวันออก' },
  { at: [128, 186], name: 'ประตูใต้' },
  { at: [162, 92], name: 'ประตูป่า' },
  { at: [103, 60], name: 'ประตูหลังหอ' },
  { at: [68, 160], name: 'ประตูเนินโจร' },
  { at: [154, 92], name: 'ประตูน้ำเหนือ', water: true },
  { at: [158, 173], name: 'ประตูน้ำใต้', water: true },
];

export interface RoadDef {
  readonly id: string;
  /** Width (metres): roads 4, the link road 3, trails 2, ledge paths 1. */
  readonly w: number;
  readonly pts: readonly XY[];
}
/**
 * Roads, the link road and trails. The west and south roads run on to the map's edge, where the carriage roads leave
 * the plains (the horse stations at their ends).
 */
export const ROADS: readonly RoadDef[] = [
  { id: 'N', w: 4, pts: [[128, 116], [128, 38]] },
  { id: 'S', w: 4, pts: [[128, 140], [128, 190], [128, 254]] },
  { id: 'W', w: 4, pts: [[116, 128], [50, 128], [2, 128]] },
  { id: 'E', w: 4, pts: [[140, 126], [158, 124], [206, 124], [212, 124]] },
  { id: 'link', w: 3, pts: [[119.5, 119.5], [109.7, 105.7]] },
  { id: 't-wolf', w: 2, pts: [[160, 118], [160, 106], [163, 90], [164, 72], [176, 58], [194, 44]] },
  { id: 't-snake', w: 2, pts: [[164, 72], [160, 56]] },
  { id: 't-hall', w: 2, pts: [[112, 104], [99, 89]] },
  { id: 't-bamboo', w: 2, pts: [[103, 89], [103, 60], [100, 56], [80, 50]] },
  { id: 't-north', w: 2, pts: [[128, 84], [110, 62], [103, 58]] },
  { id: 't-bandit', w: 2, pts: [[72, 128], [70, 150], [66, 168], [58, 184]] },
  { id: 't-marsh', w: 2, pts: [[64, 166], [46, 164]] },
  { id: 't-graves', w: 2, pts: [[128, 190], [160, 184], [176, 196]] },
  { id: 't-red', w: 2, pts: [[176, 196], [208, 216], [222, 226]] },
  { id: 't-shrine', w: 2, pts: [[200, 124], [204, 146]] },
  { id: 't-bears', w: 2, pts: [[212, 124], [222, 118]] },
  { id: 't-ford', w: 2, pts: [[160, 184], [164, 176], [166, 168], [160, 160], [156, 150]] },
  { id: 't-ledge', w: 1, pts: [[40, 22], [56, 14], [74, 10]] },
  { id: 't-camp', w: 1, pts: [[66, 50], [50, 36], [40, 28]] },
];

/** The hub's edge: a low earth wall (≤ 40 px) with a hedge; gates where roads, trails and the stream cross it. */
export const HUB_WALL: readonly XY[] = [
  [62, 60], [106, 60], [106, 92], [166, 92], [166, 66], [196, 66], [196, 92], [210, 92], [210, 162], [178, 162],
  [168, 168], [124, 188], [96, 188], [72, 174], [62, 140],
];

export type RoofColour = 'red' | 'grey' | 'green' | 'wood';
/** What a structure is, as the map generator builds it (the nearest kind the kit has; step 3 draws them as themselves). */
export type PlainsBuildingKind =
  | 'hall'
  | 'paifang'
  | 'gate'
  | 'inn'
  | 'stage'
  | 'pagoda'
  | 'jail'
  | 'shop'
  | 'warehouse'
  | 'trainhall'
  | 'stall'
  | 'house'
  | 'stable'
  | 'shrine'
  | 'temple'
  | 'tower'
  | 'stockade'
  | 'camp'
  | 'pavilion';

export interface PlainsBuilding {
  readonly n: number;
  readonly kind: PlainsBuildingKind;
  /** Thai name. */
  readonly name: string;
  /** Footprint [cx, cy, w, h] (metres). */
  readonly rect: CRect;
  /** Art-pixel height to the roof top (the occlusion check's input). */
  readonly px: number;
  readonly roof?: RoofColour;
}

/**
 * Structures. Roof colour by function: red = homes and trade, green = temple and garden, grey = official (the
 * warehouse and the training hall are grey).
 */
export const BUILDINGS: readonly PlainsBuilding[] = [
  // Hub core.
  { n: 4, kind: 'hall', name: 'หอธงสำนัก', rect: [84, 74, 24, 14], px: 254, roof: 'grey' },
  { n: 2, kind: 'paifang', name: 'ซุ้มประตูใต้', rect: [128, 152, 8, 1], px: 128, roof: 'red' },
  { n: 0, kind: 'gate', name: 'ประตูเหนือ', rect: [128, 92, 8, 1], px: 128, roof: 'grey' },
  { n: 7, kind: 'inn', name: 'โรงเตี๊ยม', rect: [184, 104, 16, 12], px: 170, roof: 'red' },
  { n: 0, kind: 'stage', name: 'เวทีงิ้ว', rect: [166, 101, 10, 4], px: 100, roof: 'red' },
  { n: 25, kind: 'pagoda', name: 'เจดีย์', rect: [182, 80, 6, 6], px: 190, roof: 'green' },
  { n: 26, kind: 'jail', name: 'คุก', rect: [54, 72, 8, 4], px: 90, roof: 'grey' },
  // The training hall (โรงฝึกยุทธ์): the hall on the north side of its court, where the yard was.
  { n: 5, kind: 'trainhall', name: 'โรงฝึกยุทธ์', rect: [86, 110, 14, 6], px: 150, roof: 'grey' },
  // Market: the warehouse (โรงคลัง) first from the plaza, then tall shophouses on the north (back) row, low stalls on
  // the south (front) row.
  { n: 6, kind: 'warehouse', name: 'โรงคลัง', rect: [162, 116, 6, 6], px: 136, roof: 'grey' },
  { n: 6, kind: 'shop', name: 'หมอยา', rect: [170, 116, 6, 6], px: 136, roof: 'grey' },
  { n: 6, kind: 'shop', name: 'ร้านผ้า', rect: [178, 116, 6, 6], px: 136, roof: 'grey' },
  { n: 6, kind: 'shop', name: 'ตำรา', rect: [194, 116, 6, 6], px: 136, roof: 'grey' },
  { n: 6, kind: 'shop', name: 'ตีเหล็ก', rect: [202, 116, 6, 6], px: 136, roof: 'grey' },
  { n: 6, kind: 'stall', name: 'ประกาศ', rect: [162, 131, 6, 5], px: 40 },
  { n: 6, kind: 'stall', name: 'ครัว', rect: [170, 131, 6, 5], px: 40 },
  { n: 6, kind: 'stall', name: 'ของชำ', rect: [178, 131, 6, 5], px: 40 },
  { n: 6, kind: 'stall', name: 'แผงผู้เล่น', rect: [190, 131, 14, 5], px: 40 },
  { n: 6, kind: 'stall', name: 'แผงผู้เล่น', rect: [203, 131, 6, 5], px: 40 },
  // Homes: six houses, four silhouettes.
  { n: 10, kind: 'house', name: 'บ้านมีลานใน', rect: [84, 150, 12, 10], px: 136, roof: 'grey' },
  { n: 10, kind: 'house', name: 'เรือนยาว', rect: [102, 147, 14, 6], px: 120, roof: 'grey' },
  { n: 10, kind: 'house', name: 'บ้าน', rect: [115, 152, 6, 6], px: 136, roof: 'grey' },
  { n: 10, kind: 'house', name: 'เรือนใต้ถุนสูง', rect: [84, 164, 6, 6], px: 120, roof: 'grey' },
  { n: 10, kind: 'house', name: 'บ้าน', rect: [98, 162, 6, 6], px: 136, roof: 'grey' },
  { n: 10, kind: 'house', name: 'บ้าน', rect: [110, 164, 6, 6], px: 136, roof: 'grey' },
  { n: 11, kind: 'stable', name: 'คอกม้า', rect: [104, 180, 8, 6], px: 90, roof: 'grey' },
  // Outer grounds.
  // The field shrine (row 13) is the training spot's own (data/trainingSpots.ts `liu-shrine` at (204.5,152.5): its solid
  // ground from map.ts shapeTrainingSpots, its ruined shrine drawn by the client). A kit building here drew it twice.
  { n: 16, kind: 'temple', name: 'วิหารร้าง', rect: [30, 110, 16, 10], px: 160, roof: 'green' },
  { n: 16, kind: 'tower', name: 'หอระฆัง', rect: [16, 102, 5, 5], px: 216, roof: 'green' },
  { n: 15, kind: 'stockade', name: 'ค่ายโจร', rect: [50, 210, 24, 18], px: 64, roof: 'wood' },
  { n: 20, kind: 'camp', name: 'ค่ายเงา', rect: [34, 26, 20, 16], px: 64, roof: 'wood' },
  { n: 18, kind: 'shrine', name: 'ศาลเจ้าสุสาน', rect: [196, 204, 6, 5], px: 110, roof: 'grey' },
  // Missing building types (BUILDINGS-R1): field cottages, a timber townhouse, a moon gate, a drum tower, the bandit
  // watchtower and camp gate. Painted by name (apps/client0 kit/extra.ts).
  { n: 12, kind: 'house', name: 'กระท่อมมุงจาก', rect: [100, 196, 5, 5], px: 100, roof: 'wood' },
  { n: 12, kind: 'house', name: 'กระท่อมมุงจาก', rect: [146, 196, 5, 5], px: 100, roof: 'wood' },
  { n: 10, kind: 'house', name: 'เรือนไม้สองชั้น', rect: [90, 178, 6, 6], px: 156, roof: 'grey' },
  { n: 25, kind: 'gate', name: 'ประตูพระจันทร์', rect: [172, 88, 4, 1], px: 90, roof: 'grey' },
  { n: 4, kind: 'tower', name: 'หอกลอง', rect: [114, 78, 6, 6], px: 200, roof: 'grey' },
  { n: 15, kind: 'tower', name: 'หอสังเกตการณ์ไม้', rect: [70, 200, 4, 4], px: 150, roof: 'wood' },
  { n: 15, kind: 'gate', name: 'ประตูค่ายโจร', rect: [50, 199, 4, 1], px: 92, roof: 'wood' },
  // Wayside pavilions (row 27).
  { n: 27, kind: 'pavilion', name: 'ศาลา', rect: [134, 98, 4, 4], px: 120, roof: 'green' },
  { n: 27, kind: 'pavilion', name: 'ศาลา', rect: [64, 134, 4, 4], px: 120, roof: 'green' },
  { n: 27, kind: 'pavilion', name: 'ศาลา', rect: [60, 170, 4, 4], px: 120, roof: 'green' },
  { n: 27, kind: 'pavilion', name: 'ศาลา', rect: [134, 180, 4, 4], px: 120, roof: 'green' },
  { n: 27, kind: 'pavilion', name: 'ศาลา', rect: [214, 132, 4, 4], px: 120, roof: 'green' },
  { n: 27, kind: 'pavilion', name: 'ศาลา', rect: [158, 96, 4, 4], px: 120, roof: 'green' },
  { n: 27, kind: 'pavilion', name: 'ศาลา', rect: [152, 186, 4, 4], px: 120, roof: 'green' },
  { n: 27, kind: 'pavilion', name: 'ศาลา', rect: [100, 52, 4, 4], px: 120, roof: 'green' },
];

/**
 * Safe circles (C0_SCENE_PLAINS §6, C0_TOWN_LAYOUT §5): no PK or kidnapping inside, monsters give up the chase.
 * `main`: the wide plaza circle every map has (the user's safe-zone rule).
 */
export interface PlainsSafeCircle {
  readonly id: string;
  readonly name: string;
  readonly at: XY;
  readonly r: number;
  readonly main?: boolean;
}
export const SAFE: readonly PlainsSafeCircle[] = [
  { id: 'plains-plaza', name: 'ลานศิลาเก้ากระบี่', at: [128, 128], r: 13, main: true },
  { id: 'plains-ring', name: 'ลานประลอง', at: [104, 100], r: 10 },
  { id: 'plains-hall', name: 'หอธงสำนัก', at: [84, 78], r: 14 },
  { id: 'plains-shrine', name: 'ศาลร้างกลางทุ่ง', at: [204, 152], r: 5 },
];

/** The plaza (1) and the duel ring (3). */
export const PLAZA = { at: [128, 128] as XY, r: 12, dais: 3.2, swords: 4.2, lanterns: 11.6, posts: 10.6 } as const;
export const RING = { at: [104, 100] as XY, r: 8, floor: 6, mark: 2.2 } as const;

/** Plaza rim posts (angle in degrees: 0 = +x east, 90 = +y south), low stalls and signs between the lanterns. */
export const POSTS: readonly { readonly a: number; readonly name: string }[] = [
  { a: -20, name: 'กระดานประกาศ' },
  { a: 20, name: 'คลังฝากของ' },
  { a: 160, name: 'แผงยา' },
  { a: 200, name: 'กลองเตือนบอสโลก' },
  { a: 250, name: 'ป้ายแผนที่' },
  { a: 70, name: 'แผงน้ำชา' },
];

/** Danger tiers (C0_OPENING_PLAINS §11.2; no player levels). */
export type DangerTier = 1 | 2 | 3 | 4 | 5;

export interface MonsterPocket {
  readonly id: string;
  /** Thai, for the map. */
  readonly name: string;
  /** The §4.3 place it belongs to. */
  readonly at: number;
  readonly tier: DangerTier;
  /** Spawn rectangle [x0, x1, y0, y1) (metres). */
  readonly box: Box;
  /** How many at once. */
  readonly n: number;
  /** Monster ids (data/monsters.ts) that exist today; empty: still to make (Systems' monster table). */
  readonly monsters: readonly string[];
}

/**
 * Monster pockets. Each keeps ≥ 19 m from the plaza, ≥ 14 m from the ring and ≥ r + 6 from every safe circle; no
 * spawn inside r + 4 of one (the buffer rule, checked by test/plains256.test.ts). Pockets are ≥ 40 m apart.
 */
export const POCKETS: readonly MonsterPocket[] = [
  { id: 'K1', name: 'หมาป่าเงาจันทร์', at: 14, tier: 1, box: [180, 232, 18, 62], n: 18, monsters: ['wolf'] },
  // South of the farm, so the paddies stay a calm life scene (buffalo, farmers); boars raiding the paddy edge come
  // later as an NPC-life event (main, 2026-10-10).
  { id: 'K2', name: 'หมูป่าเขี้ยวเหล็ก', at: 12, tier: 1, box: [96, 122, 224, 250], n: 12, monsters: ['boar'] },
  { id: 'K3', name: 'งูพิษหยก', at: 8, tier: 2, box: [154, 166, 34, 56], n: 6, monsters: ['viper'] },
  { id: 'K4', name: 'โจรภูเขา · โจรมีดบิน', at: 15, tier: 2, box: [26, 78, 182, 226], n: 18, monsters: ['bandit', 'knifer'] },
  { id: 'K5', name: 'คนหน้ากากไม้', at: 20, tier: 2, box: [56, 78, 42, 56], n: 8, monsters: [] },
  { id: 'K6', name: 'นักฆ่าเงาดำ', at: 20, tier: 3, box: [24, 44, 18, 34], n: 6, monsters: ['assassin'] },
  { id: 'K7', name: 'พระนอกรีต · แมงมุมหน้าผี', at: 16, tier: 3, box: [14, 46, 100, 132], n: 14, monsters: ['monk', 'spider'] },
  { id: 'K8', name: 'หมีดำ', at: 17, tier: 3, box: [220, 246, 92, 146], n: 10, monsters: ['bear', 'whitetiger'] },
  { id: 'K9', name: 'ผีกระโดด · ค้างคาวโลหิต', at: 18, tier: 3, box: [172, 208, 200, 230], n: 14, monsters: ['jiangshi', 'bloodbat'] },
  { id: 'K10', name: 'งูเหลือมยักษ์', at: 24, tier: 4, box: [20, 40, 156, 172], n: 1, monsters: ['python'] },
  { id: 'K11', name: 'ปีศาจกระบี่ทมิฬ', at: 19, tier: 4, box: [224, 236, 226, 238], n: 1, monsters: ['boss'] },
  { id: 'K12', name: 'ลิงยักษ์ทงปี้', at: 21, tier: 5, box: [120, 136, 54, 70], n: 1, monsters: ['worldboss'] },
  { id: 'K13', name: 'จอมมารพันกระบี่', at: 22, tier: 5, box: [120, 136, 14, 30], n: 1, monsters: [] },
];

/** The buffers of the safe-zone rule (C0_SCENE_PLAINS §6 rule 2). */
export const POCKET_BUFFER = { plaza: 19, ring: 14, safe: 6 } as const;

/** Guards (ทหารยาม): posted at the hub's gates and the plaza; they attack red names (T3 builds the rules). */
export const GUARD_POSTS: readonly { readonly id: string; readonly at: XY; readonly face: XY }[] = [
  { id: 'g-north', at: [125, 94], face: [0, -1] },
  { id: 'g-west', at: [64, 125], face: [-1, 0] },
  { id: 'g-east', at: [208, 121], face: [1, 0] },
  { id: 'g-south', at: [125, 184], face: [0, 1] },
  { id: 'g-plaza-n', at: [124, 114], face: [0, 1] },
  { id: 'g-plaza-s', at: [132, 142], face: [0, -1] },
];

/** Two patrol loops (closed polylines walked in order): the market and plaza, and the homes and the west gate. */
export const PATROLS: readonly { readonly id: string; readonly pts: readonly XY[] }[] = [
  {
    id: 'p-market',
    pts: [[128, 114], [146, 124], [180, 124], [206, 124], [180, 126], [146, 128], [128, 142], [114, 128]],
  },
  {
    id: 'p-homes',
    pts: [[116, 132], [100, 140], [80, 142], [70, 150], [76, 170], [96, 172], [118, 170], [126, 156]],
  },
];

/**
 * Horse stations (สถานีม้า): the carriage stop in the hub and one at each road that leaves the map (the carriage
 * roads, T1). Data points only; the carriage rules still use data/carriage.ts.
 */
export const HORSE_STATIONS: readonly { readonly id: string; readonly name: string; readonly at: XY; readonly edge?: 'w' | 's' }[] = [
  { id: 'hs-carriage', name: 'ท่ารถม้า', at: [110, 178] },
  { id: 'hs-west', name: 'สถานีม้าทางตะวันตก', at: [8, 124], edge: 'w' },
  { id: 'hs-south', name: 'สถานีม้าทางใต้', at: [132, 248], edge: 's' },
];

export const VISTAS: readonly XY[] = [[84, 64], [150, 126], [196, 200]];
export const CHESTS: readonly XY[] = [[22, 140], [242, 60], [70, 230], [168, 34]];

/** What an NPC does at a life station (the folk schedules' stations, data/folk.ts; the NPC-life lane builds them). */
export type LifeActivity = 'pray' | 'play' | 'graze' | 'wallow' | 'boat' | 'fish' | 'wash';

export interface LifeStation {
  readonly id: string;
  /** Thai name. */
  readonly name: string;
  readonly act: LifeActivity;
  readonly at: XY;
  /** How far round `at` the NPCs spread (metres). */
  readonly r: number;
  /** How many NPCs (or animals) it holds at most. */
  readonly n: number;
  /** A boat lane: the course boats keep to on the water (metres), in order. */
  readonly lane?: readonly XY[];
  /** It stands on the water (boats) or at its edge (fishing, washing). */
  readonly water?: boolean;
}

/**
 * NPC-life stations (user, 2026-10-10): places where townsfolk and animals live their day. Points only; the art
 * comes later. All sit outside the occlusion band of the default camera's spawn and duel views (the hub's south and
 * east edges and the outer fields), none in a monster pocket.
 */
export const LIFE_STATIONS: readonly LifeStation[] = [
  // A small shrine in the homes district by the south gate: a courtyard with an incense burner.
  { id: 'l-town-shrine', name: 'ศาลเจ้าประจำย่าน', act: 'pray', at: [120, 168], r: 3, n: 3 },
  // Children's play squares: beside the opera square, and a small one in front of the homes.
  { id: 'l-play-opera', name: 'ลานเด็กเล่นข้างลานงิ้ว', act: 'play', at: [154, 106], r: 4, n: 5 },
  { id: 'l-play-homes', name: 'ลานเด็กเล่นหน้าบ้าน', act: 'play', at: [100, 140], r: 2.5, n: 3 },
  // Water buffalo in the paddies, with a wallow pool.
  { id: 'l-buffalo-1', name: 'ควายในนา', act: 'graze', at: [112, 198], r: 4, n: 1 },
  { id: 'l-buffalo-2', name: 'ควายในนา', act: 'graze', at: [142, 208], r: 4, n: 1 },
  { id: 'l-buffalo-wallow', name: 'ปลักควาย', act: 'wallow', at: [150, 194], r: 2.5, n: 1, water: true },
  // Boat lanes on the stream and the lotus pond, small boats on them, fishermen on the banks.
  {
    id: 'l-boat-stream',
    name: 'ทางเรือลำธาร',
    act: 'boat',
    at: [150, 140],
    r: 1,
    n: 2,
    water: true,
    lane: [[150, 112], [150, 124], [150, 140], [154, 156], [162, 172]],
  },
  { id: 'l-boat-pond', name: 'เรือในบ่อบัว', act: 'boat', at: [164, 148], r: 1, n: 1, water: true, lane: [[158, 148], [164, 146], [170, 148], [164, 151]] },
  { id: 'l-fish-pond', name: 'ที่ตกปลาบ่อบัว', act: 'fish', at: [164, 155], r: 1.5, n: 2, water: true },
  { id: 'l-fish-stream', name: 'ที่ตกปลาลำธาร', act: 'fish', at: [148, 150], r: 1.5, n: 2, water: true },
  // Washing steps on the stream bank, by the bridge.
  { id: 'l-wash', name: 'ท่าซักผ้า', act: 'wash', at: [146, 134], r: 2, n: 3, water: true },
];

/**
 * The kit's props on the 256 m plains (lane C0-PROPS placed them, render/kit/placement.ts; moved here so the rules can
 * stand on them, lane C0-P1): map metres, `solid` ones block walking (plainsMap.ts lays their tiles solid). Every tall
 * prop keeps out of the occlusion band of a fighting space (scripts/lanes/c0-props/occlusion.mts).
 */
export type PlainsPropId =
  | 'lanternPost' | 'stoneLantern' | 'bannerRed' | 'bannerBlue' | 'bannerJade' | 'bannerGold' | 'bannerWhite'
  | 'bannerBlack' | 'weaponRack' | 'dummy' | 'dummyStraw' | 'mat' | 'guardStand' | 'brazier' | 'gatePost' | 'warn1'
  | 'warn2' | 'warn3' | 'warn4' | 'warn5' | 'chest' | 'well' | 'cart' | 'fenceX' | 'fenceY' | 'incense' | 'toys'
  | 'hopscotch' | 'washSteps' | 'dryingLine' | 'fishing' | 'bench' | 'hitchRail' | 'hayBales' | 'hayPile' | 'goods'
  | 'signpost' | 'swordStone' | 'swordStoneEmpty' | 'steppingStone'
  | 'tombstone' | 'tombstoneTall' | 'palisadeX' | 'palisadeY' | 'brokenBell' | 'logPile' | 'campfire' | 'rocks' | 'rocks2'
  | 'stoneRailX' | 'lanternPole2' | 'lionL' | 'lionR' | 'altar' | 'stupa' | 'torchPost' | 'bambooFenceX' | 'bambooFenceY'
  | 'warDrum'
  | 'runnerShortX' | 'runnerShortY' | 'runnerLongX' | 'runnerLongY' | 'ceremonyX' | 'ceremonyY' | 'doorRugX' | 'doorRugY' | 'stageCarpet';

export interface PlacedProp {
  readonly id: PlainsPropId;
  readonly at: XY;
  /** It blocks walking. */
  readonly solid?: boolean;
}

/**
 * The three stepping stones of the F2 qinggong route over the stream (y 98.5, tiles x 150, 152, 154; a 1 m gap of
 * water between them). Each lays a walkable stone tile in the water (plainsMap.ts) with a `steppingStone` prop on it.
 */
export const STEPPING_STONES: readonly XY[] = [[150.5, 98.5], [152.5, 98.5], [154.5, 98.5]];

/** Danger tier of the ground each trailhead gate leads to (C0_TOWN_LAYOUT §5). */
const GATE_TIER: Record<string, PlainsPropId> = {
  ประตูตะวันตก: 'warn3',
  ประตูตะวันออก: 'warn3',
  ประตูใต้: 'warn1',
  ประตูป่า: 'warn1',
  ประตูหลังหอ: 'warn2',
  ประตูเนินโจร: 'warn2',
};

const SECT_BANNERS: PlainsPropId[] = ['bannerRed', 'bannerBlue', 'bannerJade', 'bannerGold', 'bannerWhite', 'bannerBlack'];

function buildProps(): PlacedProp[] {
  const out: PlacedProp[] = [];
  const put = (id: PlainsPropId, x: number, y: number, solid = false) => out.push({ id, at: [x, y], solid });

  // หอธงสำนัก: six sect banners along the terrace front (the fair's recruiters stand at them), lanterns at the steps.
  [70, 75, 80, 88, 93, 98].forEach((x, k) => put(SECT_BANNERS[k]!, x + 0.5, 86.5, true));
  put('stoneLantern', 82.5, 87.5, true);
  put('stoneLantern', 86.5, 87.5, true);

  // โรงฝึกยุทธ์'s court (x 73–99, y 113–125): dummies on its west and east edges, mats, racks by the hall.
  for (const y of [116, 119.5, 123]) {
    put('dummy', 75.5, y, true);
    put('dummyStraw', 97.5, y, true);
  }
  for (const x of [80.5, 86.5, 92.5]) put('mat', x, 120.5);
  put('weaponRack', 78.5, 114.2, true);
  put('weaponRack', 94.5, 114.2, true);
  put('brazier', 74.5, 113.8, true);
  put('brazier', 98.5, 113.8, true);

  // Red carpets (kit/carpet.ts, flat, walkable): the training hall's aisle from its door, the hall of banners' stair
  // runner, a welcome strip south of the north gate.
  put('runnerShortY', 86, 116.3);
  put('runnerShortY', 84, 84.5);
  put('runnerShortY', 128, 95.6);

  // The market street (y 120–128): lantern posts on its north edge between the shophouses, goods by the warehouse.
  for (const x of [166, 174, 186, 198, 206]) put('lanternPost', x + 0.5, 119.6, true);
  put('goods', 158.5, 119.5, true);
  put('cart', 209.5, 128.5, true);
  put('goods', 186.5, 133.8, true);

  // The opera square (x 160–172, y 99–109): benches facing the stage; the children's corner beside it.
  for (const y of [105.5, 107.5]) for (const x of [162.5, 166.5, 170.5]) put('bench', x, y);
  put('toys', 154.5, 106.5);
  put('hopscotch', 156.5, 108.5);

  // Guards: a spear stand and a brazier beside each post.
  for (const g of GUARD_POSTS) {
    put('guardStand', g.at[0] + 1.6 + 0.5, g.at[1] + 0.5, true);
  }

  // The hub's gates: low red posts either side, the trailhead's warning sign outside.
  for (const g of GATES) {
    if (g.water || g.name === 'ประตูเหนือ') continue;
    const vertical = g.name === 'ประตูตะวันตก' || g.name === 'ประตูตะวันออก' || g.name === 'ประตูเนินโจร';
    const [x, y] = g.at;
    if (vertical) {
      put('gatePost', x + 0.5, y - 2.5, true);
      put('gatePost', x + 0.5, y + 3.5, true);
    } else {
      put('gatePost', x - 2.5, y + 0.5, true);
      put('gatePost', x + 3.5, y + 0.5, true);
    }
    const sign = GATE_TIER[g.name];
    if (sign) put(sign, vertical ? x + (x < 128 ? -1.5 : 2.5) : x + 4.5, vertical ? y + 4.5 : y + (y < 128 ? -1.5 : 2.5), true);
  }

  // The F2 stepping stones over the stream (walkable stone tiles, plainsMap.ts).
  for (const [x, y] of STEPPING_STONES) put('steppingStone', x, y);

  // Chests in the far corners.
  for (const [x, y] of CHESTS) put('chest', x + 0.5, y + 0.5, true);

  // Life stations (C0_NPC_LIFE): the town shrine's burner, the play squares, the wallow, washing and fishing spots.
  for (const l of LIFE_STATIONS) {
    const [x, y] = l.at;
    switch (l.act) {
      case 'pray':
        put('incense', x + 0.5, y - 1.5, true);
        put('stoneLantern', x - 1.5, y + 2.5, true);
        put('stoneLantern', x + 2.5, y + 2.5, true);
        break;
      case 'play':
        if (l.id === 'l-play-homes') {
          put('hopscotch', x + 0.5, y + 0.5);
          put('toys', x + 2, y - 0.5);
        }
        break;
      case 'wallow':
        put('hayPile', x + 3, y - 2);
        put('fenceX', x - 0.5, y + 3.5, true);
        break;
      case 'wash':
        put('washSteps', x + 0.5, y + 0.5);
        put('dryingLine', x - 2, y + 2.5, true);
        break;
      case 'fish':
        put('fishing', x + 0.5, y + 0.5);
        break;
    }
  }

  // Homes: a well between the houses, a cart by the long house; the carriage stop's rail, trough, hay and fence.
  put('well', 93.5, 156.5, true);
  put('cart', 118.5, 145.5, true);
  put('hitchRail', 114.5, 175.5, true);
  put('hayBales', 118.5, 183.5, true);
  put('hayPile', 101.5, 175.5);
  for (const x of [100.5, 102.5, 104.5, 106.5]) put('fenceX', x, 171.4, true);

  // Signposts at the forks just outside the hub's middle, and lantern posts down the south road.
  put('signpost', 131.5, 158.5, true);
  put('signpost', 145.5, 129.5, true);
  for (const y of [162, 172, 182]) {
    put('lanternPost', 125.2, y + 0.5, true);
    put('lanternPost', 131.8, y + 0.5, true);
  }
  // The plaza: 16 stone lanterns on its rim (R 11.6), none on the four roads' axes; the nine sword stones round the
  // stele's dais (R 4.2), the ninth groove empty (C0_SCENE_PLAINS §5). Low stones: not solid.
  for (let k = 0; k < 16; k++) {
    const a = ((k + 0.5) / 16) * Math.PI * 2;
    put('stoneLantern', PLAZA.at[0] + 0.5 + Math.cos(a) * PLAZA.lanterns, PLAZA.at[1] + 0.5 + Math.sin(a) * PLAZA.lanterns, true);
  }
  for (let k = 0; k < 9; k++) {
    const a = (k / 9) * Math.PI * 2 - Math.PI / 2;
    put(k === 8 ? 'swordStoneEmpty' : 'swordStone', PLAZA.at[0] + 0.5 + Math.cos(a) * PLAZA.swords, PLAZA.at[1] + 0.5 + Math.sin(a) * PLAZA.swords);
  }
  return out;
}


/** The kit props of the 256 m plains, in placing order. */
export const PLAINS_PROPS: readonly PlacedProp[] = [...buildProps(), ...buildOuter(ROADS, BRIDGE.at)];
